import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { useActivityIndicator } from '#src/hooks/users/useActivityIndicator.ts';

import type { ActivityIndicator_user } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
  --radius: var(--half-unit);
  --stroke: 1px;
  --gap: 2px;
  --offset: calc(var(--radius) / 2);
  --offset-right: var(--offset);
  --offset-bottom: var(--offset);
`;

const TooltipStyled = styled(Tooltip)`
  display: inline-block;
  position: absolute;
  right: calc(-1 * (var(--radius) - var(--offset-right)));
  bottom: calc(-1 * (var(--radius) - var(--offset-bottom)));
`;

const ActivityIcon = styled.div`
  background-color: var(--c-onBase-success);
  width: calc(2 * var(--radius));
  height: calc(2 * var(--radius));
  border-radius: 50%;
  box-sizing: content-box;
  border: 2px solid var(--c-black);
`;

const messages = defineMessages({
  online: {
    id: 'User.Header.online',
    defaultMessage: 'Online',
  },
});

type Props = {
  user: ActivityIndicator_user;
  children: React.JSX.Element;
  disableClick?: React.ComponentProps<typeof Tooltip>['disableClick'];
  style?: React.CSSProperties;
};

export const ActivityIndicator = ({
  user,
  children,
  disableClick,
  style,
}: Props) => {
  const { formatMessage } = useIntl();
  const active = useActivityIndicator(user);

  if (!active) {
    return children;
  }
  return (
    <Wrapper style={style}>
      {children}
      <TooltipStyled
        title={
          <>
            {user.status}
            {!!user.status && ' ('}
            {formatMessage(messages.online)}
            {!!user.status && ')'}
          </>
        }
        placement="right"
        role="status"
        disableClick={disableClick}
      >
        <ActivityIcon />
      </TooltipStyled>
    </Wrapper>
  );
};

ActivityIndicator.fragments = {
  user: gql`
    fragment ActivityIndicator_user on PublicUserInfoInterface {
      slug
      status
      ...useActivityIndicator_user
    }
    ${useActivityIndicator.fragments.user}
  ` as TypedDocumentNode<ActivityIndicator_user>,
};
